import { z } from 'zod';
import { LocalizedStringSchema, TimestampFieldsSchema } from './common';

// ─── Channel ──────────────────────────────────────────────────────────────
// Channel ที่ Contact ใช้ติดต่อ

export const ChannelTypeSchema = z.enum([
  'line', 'whatsapp', 'wechat', 'facebook', 'web', 'email', 'phone',
]);
export type ChannelType = z.infer<typeof ChannelTypeSchema>;

// ─── Channel Identity ─────────────────────────────────────────────────────
// Identity ของ Contact ใน Channel นั้นๆ

export const ChannelIdentitySchema = z.object({
  channelType: ChannelTypeSchema,
  channelUserId: z.string(),    // LINE userId / WhatsApp phone / WeChat openid / etc.
  displayName: z.string().optional(),
  pictureUrl: z.string().url().optional(),
  language: z.enum(['th', 'en', 'zh']).optional(),
  followedAt: z.string().datetime({ offset: true }).optional(),
  lastInteractedAt: z.string().datetime({ offset: true }).optional(),
});
export type ChannelIdentity = z.infer<typeof ChannelIdentitySchema>;

// ─── Contact ──────────────────────────────────────────────────────────────
// Unified identity — คนๆ เดียวที่ติดต่อผ่านหลาย channel
// สำคัญมาก: LINE userId, WhatsApp phone, WeChat openid → Contact เดียวกัน

export const ContactSchema = z
  .object({
    id: z.string(),
    tenantId: z.string(),

    // Profile (กรอกโดย user หรือ staff)
    profile: z.object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      displayName: z.string().optional(),
      email: z.string().email().optional(),
      phone: z.string().optional(),
      nationality: z.string().optional(),
      dateOfBirth: z.string().optional(),
      gender: z.enum(['male', 'female', 'other']).optional(),
      address: z.object({
        line1: z.string().optional(),
        city: z.string().optional(),
        country: z.string().optional(),
        postalCode: z.string().optional(),
      }).optional(),
      preferredLanguage: z.enum(['th', 'en', 'zh']).optional(),
      notes: z.string().optional(),
    }).default({}),

    // Channel identities — คน 1 คน อาจมาจากหลาย channel
    channels: z.record(ChannelTypeSchema, ChannelIdentitySchema).default({}),
    // Example:
    // channels.line = { channelType: 'line', channelUserId: 'U1234abc', displayName: 'สมชาย' }
    // channels.whatsapp = { channelType: 'whatsapp', channelUserId: '+66812345678' }

    // CRM
    tags: z.array(z.string()).default([]),
    segment: z.string().optional(),
    loyaltyPoints: z.number().int().nonnegative().default(0),
    totalSpend: z.number().nonnegative().default(0),
    totalBookings: z.number().int().nonnegative().default(0),
    lastBookingAt: z.string().datetime({ offset: true }).optional(),

    // Custom fields (industry-specific)
    customFields: z.record(z.string(), z.unknown()).default({}),

    isActive: z.boolean().default(true),
  })
  .merge(TimestampFieldsSchema);

export type Contact = z.infer<typeof ContactSchema>;

export const CreateContactInputSchema = ContactSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  totalSpend: true,
  totalBookings: true,
  lastBookingAt: true,
});
export type CreateContactInput = z.infer<typeof CreateContactInputSchema>;

// ─── Helper ───────────────────────────────────────────────────────────────
// หา contact จาก channel-specific ID

export type ContactLookupKey =
  | { channel: 'line'; userId: string }
  | { channel: 'whatsapp'; phone: string }
  | { channel: 'wechat'; openId: string }
  | { channel: 'web'; email: string }
  | { channel: 'facebook'; psid: string };
